import { useEffect, useState } from "react";
import { FilePlus2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatErr } from "@/lib/api";

export function TopicPlanner({ onDrafted }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(null);
  const [lang, setLang] = useState("en");
  const draft = async (t, i) => {
    setDrafting(i);
    try { await api.post("/admin/workshops/draft", { topic: t.topic, examples: t.examples || [], lang }); toast.success("Draft workshop created above. Edit the library and date, download the flyer, then publish."); onDrafted?.(); }
    catch (e) { toast.error(formatErr(e)); } finally { setDrafting(null); }
  };
  useEffect(() => { api.get("/admin/workshops/topics/summary").then((r) => setD(r.data)).catch((e) => setD({ topics: [], answers_total: 0, error: formatErr(e) })); }, []);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/workshops/topics/summary"); setD(r.data); toast.success("Topics updated"); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (!d) return null;
  const fresh = d.answers_total - (d.answers_used || 0);
  const max = Math.max(1, ...d.topics.map((t) => t.count));
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4" data-testid="topic-planner">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div><h3 className="font-semibold text-slate-900">Top requested topics</h3>
          <p className="text-xs text-slate-500">Grouped from {d.answers_used || 0} "What should we cover next?" answers{fresh > 0 ? ` · ${fresh} new since last update` : ""}.</p></div>
        <Button size="sm" onClick={run} disabled={busy || !d.answers_total} className="bg-emerald-600 hover:bg-emerald-700" data-testid="topic-planner-run">
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}{d.topics.length ? "Update topics" : "Group answers"}</Button>
      </div>
      {d.error && <p className="text-sm text-red-600 mt-3" data-testid="topic-planner-error">{d.error}</p>}
      {!d.answers_total && !d.error && <p className="text-sm text-slate-500 mt-3" data-testid="topic-planner-empty">No written survey answers yet.</p>}
      {d.topics.length > 0 && (
        <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">Draft outline and flyer in
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="h-8 border border-slate-300 rounded-md px-2 text-xs bg-white" data-testid="topic-draft-lang"><option value="en">English</option><option value="es">Spanish</option></select></div>
      )}
      {d.topics.length > 0 && (
        <ol className="mt-3 space-y-3" data-testid="topic-planner-list">
          {d.topics.map((t, i) => (
            <li key={`${i}-${t.topic}`} data-testid={`topic-${i}`}>
              <div className="flex items-center gap-3 text-sm"><span className="font-semibold text-slate-900 flex-1">{i + 1}. {t.topic}</span><span className="text-slate-500">{t.count}</span>
                <Button size="sm" variant={i === 0 ? "default" : "outline"} onClick={() => draft(t, i)} disabled={drafting !== null} className={`h-8 ${i === 0 ? "bg-emerald-600 hover:bg-emerald-700" : ""}`} data-testid={`topic-draft-${i}`}>
                  {drafting === i ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FilePlus2 className="h-4 w-4 mr-1" />}Draft workshop</Button></div>
              <div className="h-1.5 bg-slate-100 rounded mt-1"><div className="h-1.5 bg-emerald-500 rounded" style={{ width: `${(100 * t.count) / max}%` }} /></div>
              {t.examples?.length > 0 && <p className="text-xs text-slate-500 mt-1 italic">"{t.examples.join('" · "')}"</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
